/* Brand navigation and exact reference matching. No fuzzy matching of reference digits. */
(() => {
  const data = window.WellstrapsSearchData || { brands: [], models: [] };
  let configElement;
  let configText;
  let configValue = {};
  const fold = (value) => String(value || '').normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim().replace(/\s+/g, ' ');
  const pack = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const escapePattern = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const phrasePattern = (value) => new RegExp(`(^|[^\\p{L}\\p{N}])${escapePattern(value).replace(/ /g, '[\\s-]+')}(?=$|[^\\p{L}\\p{N}])`, 'giu');
  const normalize = (value) => String(value || '').trim().replace(/\s+/g, ' ').replace(/\b(\d{1,2}(?:\.\d+)?)\s*(?:mm|мм)(?=$|[\s,;/)])/gi, '$1mm');

  function config() {
    const element = document.getElementById('WellstrapsSearchConfig');
    if (element !== configElement || element?.textContent !== configText) {
      configElement = element;
      configText = element?.textContent;
      try { configValue = JSON.parse(configText || '{}'); } catch { configValue = {}; }
    }
    return configValue;
  }

  const enabled = () => Boolean(configElement || document.getElementById('WellstrapsSearchConfig')) && config().enabled !== false;
  const text = (key, values = {}) => {
    let value = config().text?.[key] || key;
    Object.entries(values).forEach(([key, replacement]) => { value = value.replace(`{${key}}`, String(replacement)); });
    return value;
  };
  function internalUrl(value) {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin && /^https?:$/.test(url.protocol) ? `${url.pathname}${url.search}${url.hash}` : null;
    } catch { return null; }
  }
  function searchUrl(query, reference) {
    const url = new URL(internalUrl(config().searchUrl) || '/search', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    if (reference) url.searchParams.set('ws_ref', reference);
    return `${url.pathname}${url.search}`;
  }
  function models() {
    const merchant = (config().references || []).filter(Boolean).map((entry) => ({
      ...entry,
      query: entry.query || `${entry.brand} ${entry.name}`.trim(),
      references: String(entry.references || '').split(/[,;\n]+/).map((r) => r.trim()).filter(Boolean),
      aliases: [],
      strict: true,
      products: (entry.products || []).filter(Boolean).map((p) => p.handle),
    }));
    return [...merchant, ...(data.models || [])];
  }
  function productMap() {
    const products = [...(config().products || []), ...(config().references || []).filter(Boolean).flatMap((entry) => entry.products || [])].filter(Boolean);
    return new Map(products.map((product) => [product.handle, product]));
  }
  function getBrands() {
    const entries = [...(data.brands || []), ...(config().references || []).filter((entry) => entry?.brand).map((entry) => ({ name: entry.brand, aliases: [] }))];
    const unique = new Map();
    entries.forEach((brand) => { if (!unique.has(fold(brand.name))) unique.set(fold(brand.name), brand); });
    return [...unique.values()];
  }
  function aliasMatch(query, aliases) {
    const normalized = ` ${fold(query)} `;
    return aliases.flatMap((alias) => [alias, alias.includes(' ') ? alias.replace(/ /g, '') : alias])
      .filter((alias) => normalized.includes(` ${fold(alias)} `))
      .sort((a, b) => b.length - a.length)[0];
  }
  function oneEdit(left, right) {
    if (Math.abs(left.length - right.length) > 1 || left === right) return false;
    if (left.length === right.length) {
      const different = [...left].map((c, i) => c === right[i] ? -1 : i).filter((i) => i >= 0);
      return different.length === 1 || (different.length === 2 && different[1] === different[0] + 1 && left[different[0]] === right[different[1]] && left[different[1]] === right[different[0]]);
    }
    const shorter = left.length < right.length ? left : right;
    const longer = left.length < right.length ? right : left;
    for (let i = 0; i < longer.length; i++) if (longer.slice(0, i) + longer.slice(i + 1) === shorter) return true;
    return false;
  }
  function resolve(value) {
    const raw = normalize(value);
    const context = { raw, query: raw, brands: [], model: null, reference: null, candidates: [], unknownReference: false };
    if (!enabled() || /["\\:]|(?:^|\s)(?:AND|OR|NOT)(?:\s|$)/.test(raw)) return context;
    const allModels = models();
    const references = new Map();
    allModels.forEach((model) => (model.references || []).forEach((reference) => {
      if (!references.has(pack(reference))) references.set(pack(reference), { model, reference });
    }));
    const referenceText = raw.replace(/^(?:ref(?:erence)?|реф(?:еренс)?)\.?\s*#?\s*/i, '');
    const tokenMatches = [...referenceText.matchAll(/[a-z0-9][a-z0-9.\/-]*/gi)];
    const tokens = tokenMatches.map((match) => match[0]);
    for (let i = 0; i < tokens.length && !context.reference; i++) {
      for (let end = i + 1; end <= Math.min(tokens.length, i + 7); end++) {
        const match = references.get(pack(tokens.slice(i, end).join('')));
        if (!match) continue;
        context.model = match.model;
        context.reference = match.reference;
        const first = tokenMatches[i];
        const last = tokenMatches[end - 1];
        const remainder = `${referenceText.slice(0, first.index)} ${referenceText.slice(last.index + last[0].length)}`.replace(phrasePattern(match.model.brand), ' ');
        context.query = normalize(`${match.model.query} ${remainder}`);
        break;
      }
    }
    context.brands = getBrands().map((brand) => ({ ...brand, matched: aliasMatch(raw, [brand.name, ...(brand.aliases || [])]) })).filter((brand) => brand.matched);
    if (!context.model) {
      const matches = allModels.map((model) => ({ model, alias: aliasMatch(raw, [model.query, ...(model.aliases || [])]) })).filter((match) => match.alias).sort((a, b) => b.alias.length - a.alias.length);
      const match = matches.find(({ model }) => !context.brands.length || context.brands.some((brand) => brand.name === model.brand) || model.id === 'moonswatch');
      if (match) {
        context.model = match.model;
        context.query = raw.replace(phrasePattern(match.alias), (_, before) => `${before}${match.model.query}`);
      }
    }
    if (context.model) context.brands = [{ name: context.model.brand }];
    if (!context.reference) {
      getBrands().forEach((brand) => {
        [...(brand.aliases || []), brand.name].sort((a, b) => b.length - a.length).forEach((alias) => {
          context.query = context.query.replace(phrasePattern(alias), (_, before) => `${before}${brand.name}`);
        });
        context.query = context.query.replace(new RegExp(`\\b${escapePattern(brand.name)}\\s+${escapePattern(brand.name)}\\b`, 'gi'), brand.name);
      });
    }
    if (!context.model) {
      const token = tokens.find((token) => pack(token).length >= 6 && (token.match(/\d/g) || []).length >= 3);
      if (token) {
        const prefix = pack(token);
        context.candidates = allModels.filter((model) => (model.references || []).some((reference) => pack(reference).startsWith(prefix))).slice(0, 3);
        context.unknownReference = context.candidates.length === 0;
        if (context.unknownReference && !/\s/.test(raw)) context.query = raw;
      }
    }
    return context;
  }
  function navigation() {
    const entries = (config().navigation || []).filter(Boolean).map((item) => ({ ...item, url: internalUrl(item.url) })).filter((item) => item.url && item.url.includes('/collections/'));
    return [...new Map(entries.map((entry) => [entry.url, entry])).values()];
  }
  function collectionFor(name) {
    const clean = (value) => fold(value).split(' ').filter((word) => !['watch', 'watches', 'strap', 'straps', 'band', 'bands', 'for', 'compatible', 'with', 'collection'].includes(word)).join(' ');
    return navigation().find((entry) => clean(entry.title) === clean(name));
  }
  function createPanel(context, prefix, options = {}) {
    if (!enabled()) return null;
    if (!context.raw && config().starters === false) return null;
    const panel = document.createElement('div');
    panel.className = 'ws-search-panel';
    let count = 0;
    let hasVerifiedProducts = false;
    const group = (label) => {
      const node = document.createElement('div'); node.className = 'ws-search-group'; node.setAttribute('role', 'group'); node.setAttribute('aria-label', label);
      const heading = document.createElement('p'); heading.className = 'ws-search-heading'; heading.textContent = label; node.append(heading); panel.append(node); return node;
    };
    const note = (value, node = panel) => { const p = document.createElement('p'); p.className = 'ws-search-note'; p.textContent = value; node.append(p); };
    const link = (node, label, url, detail) => {
      const safe = internalUrl(url); if (!safe) return;
      const a = document.createElement('a'); a.className = 'ws-search-link'; a.href = safe; a.id = `${prefix}-directory-${++count}`;
      if (!options.summary) { a.setAttribute('role', 'option'); a.setAttribute('aria-selected', 'false'); a.tabIndex = -1; }
      const title = document.createElement('span'); title.textContent = label; a.append(title);
      if (detail) { const small = document.createElement('small'); small.textContent = detail; a.append(small); }
      node.append(a);
    };
    if (context.model) {
      const label = context.reference ? `${text('identified')}: ${context.reference}` : text('models');
      const node = group(label);
      const title = document.createElement('p'); title.className = 'ws-search-model'; title.textContent = `${context.model.brand} ${context.model.name}`; node.append(title);
      if (context.model.note) note(context.model.note, node);
      const products = productMap();
      const matched = (context.model.products || []).map((handle) => products.get(handle)).filter((product) => product && product.available !== false);
      hasVerifiedProducts = matched.length > 0;
      if (matched.length) {
        note(text('listed'), node);
        matched.slice(0, 4).forEach((product) => link(node, product.title, product.url));
      } else if (context.model.strict) note(text('no_verified'), node);
    } else if (context.candidates.length) {
      const node = group(text('possible'));
      context.candidates.forEach((model) => link(node, `${model.brand} ${model.name}`, searchUrl(model.query), text('related')));
    } else if (context.unknownReference) note(text('unknown'));

    let brands = context.brands;
    if (!brands.length && !context.model && !context.unknownReference && !context.candidates.length) {
      const query = fold(context.raw);
      brands = !query ? getBrands().slice(0, 6) : getBrands().filter((brand) => [brand.name, ...(brand.aliases || [])].some((alias) => fold(alias).startsWith(query))).slice(0, 4);
      if (!brands.length && /^[a-z]{4,}$/i.test(query)) {
        const corrections = getBrands().filter((brand) => oneEdit(query, fold(brand.name)));
        if (corrections.length === 1) brands = corrections;
      }
    }
    if (brands.length) {
      const node = group(context.raw ? text('navigation') : text('brands'));
      brands.slice(0, 4).forEach((brand) => {
        const collection = collectionFor(brand.name);
        link(node, text('browse', { name: brand.name }), collection?.url || searchUrl(brand.name), collection ? text('collection') : text('search'));
      });
      if (!context.model && context.raw) {
        const choices = models().filter((model) => brands.some((brand) => brand.name === model.brand));
        choices.slice(0, 3).forEach((model) => link(node, `${model.brand} ${model.name}`, searchUrl(model.query)));
      }
    }
    const query = fold(context.raw);
    const categories = context.raw ? navigation().filter((entry) => fold(entry.title).includes(query) || (query.length >= 4 && query.split(' ').every((part) => fold(entry.title).includes(part)))).slice(0, 3) : [];
    if (categories.length) {
      const node = group(text('navigation'));
      categories.forEach((entry) => link(node, entry.title, entry.url, text('collection')));
    }
    if (/\b\d{1,2}(?:\.\d+)?mm\b/i.test(context.raw) && !context.model) note(text('size_note'));
    if (options.failed) note(text('unavailable'));
    if (context.unknownReference || (context.model?.strict && !hasVerifiedProducts) || options.failed || options.empty) {
      const contact = internalUrl(config().contactUrl);
      if (contact) link(panel, text('help'), contact);
      if (options.empty && !context.model && !context.unknownReference) note(text('try_brand'));
    }
    return panel.childElementCount ? panel : null;
  }
  function enhance(root, context, prefix) {
    if (!enabled()) return;
    if (context.model?.strict) {
      const allowed = new Set(context.model.products || []);
      root.querySelectorAll('[data-ws-product]').forEach((node) => { if (!allowed.has(node.dataset.wsProduct)) node.remove(); });
      const list = root.querySelector('[id^="predictive-search-results-products-list"]');
      if (list && !list.children.length) list.parentElement.remove();
      root.querySelector('[id^="predictive-search-results-queries-list"]')?.parentElement.remove();
    }
    const panel = createPanel(context, prefix, { empty: Boolean(root.querySelector('.predictive-search__no-results')) });
    if (panel) root.querySelector('[role="listbox"]')?.prepend(panel);
    const all = root.querySelector('[id^="predictive-search-option-search-all"]');
    if (all) all.href = searchUrl(context.query, context.reference);
    const count = root.querySelector('[data-predictive-search-live-region-count-value]');
    if (count) count.textContent = `${root.querySelectorAll('[role="option"]').length} ${text('suggestions')}`;
  }
  function prepareForm(form, value) {
    const context = resolve(value);
    let referenceInput = form.querySelector('input[name="ws_ref"]');
    if (context.reference) {
      if (!referenceInput) { referenceInput = document.createElement('input'); referenceInput.type = 'hidden'; referenceInput.name = 'ws_ref'; form.append(referenceInput); }
      referenceInput.value = context.reference;
    } else referenceInput?.remove();
    return context.query;
  }
  window.WellstrapsSearch = { enabled, config, resolve, createPanel, enhance, prepareForm, searchUrl, normalize };

  class WellstrapsSearchSummary extends HTMLElement {
    connectedCallback() {
      if (!enabled()) return;
      const query = this.dataset.query || '';
      const reference = new URLSearchParams(window.location.search).get('ws_ref');
      const identified = reference ? resolve(reference) : null;
      const matchesReference = identified?.reference && (`${fold(query)} `).startsWith(`${fold(identified.query)} `);
      const context = matchesReference ? { ...identified, query } : resolve(query);
      const panel = createPanel(context, 'search-page', { summary: true });
      if (panel && context.raw) this.replaceChildren(panel);
    }
  }
  customElements.define('wellstraps-search-summary', WellstrapsSearchSummary);
})();
